import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ProductBaseConfig } from '../../types/productBase';
import { getProductData } from '../../utils/productTranslations';
import './ProductPage.css';

interface ProductPageProps {
  config: ProductBaseConfig;
}

const ProductPage: React.FC<ProductPageProps> = ({ config }) => {
  const { t } = useTranslation();
  const [comingSoonLink, setComingSoonLink] = useState<string | null>(null);

  // Get product data from i18n
  const productData = getProductData(t, config.i18nKey);
  const screenshots = config.screenshots ?? [];
  const comingSoonStores = Boolean(config.comingSoonStores);
  const comingSoonPlayStore = Boolean(config.comingSoonPlayStore);

  const showComingSoon = (name: string) => {
    setComingSoonLink(name);
    window.setTimeout(() => {
      setComingSoonLink((current) => (current === name ? null : current));
    }, 2500);
  };

  const comingSoonButton = (name: string, label: string, extraClass = 'btn-primary') => (
    <button
      key={name}
      type="button"
      className={`btn ${extraClass} btn-large store-coming-soon${comingSoonLink === name ? ' is-active' : ''}`}
      onClick={() => showComingSoon(name)}
      aria-label={`${label} – ${t('footer.comingSoon')}`}
      title={t('footer.comingSoon') as string}
    >
      {label}
      {comingSoonLink === name && (
        <span className="store-coming-soon-tooltip">{t('footer.comingSoon')}</span>
      )}
    </button>
  );

  // Get screenshot label from i18n or fallback to config
  const getScreenshotLabel = (screenshot: NonNullable<ProductBaseConfig['screenshots']>[number], index: number) => {
    if (screenshot.i18nLabelKey) {
      const screenshotsResult = t(`products.${config.i18nKey}.screenshots`, { returnObjects: true });
      const screenshots = Array.isArray(screenshotsResult) 
        ? (screenshotsResult as Array<{ label: string }>)
        : null;
      if (screenshots && screenshots[index]?.label) {
        return screenshots[index].label;
      }
    }
    return screenshot.label || 'Screenshot';
  };

  return (
    <div className="product-page">
      {/* Hero Section */}
      <section className="product-hero-section">
        <div className="container">
          <div className="product-hero-container">
            <div className="product-hero-content">
              {config.logo && (
                <div className="product-hero-logo">
                  <img src={config.logo} alt={productData.name} />
                </div>
              )}
              <h1 className="product-hero-title">{productData.name}</h1>
              <p className="product-hero-subtitle">{productData.subtitle}</p>
              <div className="product-hero-actions">
                {comingSoonStores ? (
                  <>
                    {comingSoonButton('play', t('product.viewOnPlayStore', 'View on Google Play'))}
                    {comingSoonButton('appstore', t('product.viewOnAppStore', 'View on App Store'))}
                    {comingSoonButton('web', t('product.tryOnWeb', 'Try it on Web'), 'btn-secondary')}
                  </>
                ) : (
                  <>
                    {comingSoonPlayStore
                      ? comingSoonButton('play', t('product.viewOnPlayStore', 'View on Google Play'))
                      : config.playStoreUrl && (
                      <a
                        href={config.playStoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-large"
                      >
                        {t('product.viewOnPlayStore', 'View on Google Play')}
                      </a>
                    )}
                    {config.appStoreUrl && (
                      <a
                        href={config.appStoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-large"
                      >
                        {t('product.viewOnAppStore', 'View on App Store')}
                      </a>
                    )}
                    {config.webAppUrl && (
                      <a
                        href={config.webAppUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary btn-large"
                      >
                        {t('product.tryOnWeb', 'Try it on Web')}
                      </a>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="product-about-section">
        <div className="container">
          <div className="product-about-content">
            <h2 className="section-title">{productData.aboutTitle}</h2>
            {productData.aboutParagraphs.map((paragraph, index) => (
              <p 
                key={index}
                className="product-about-text"
                dangerouslySetInnerHTML={{ __html: paragraph }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="product-features-section">
        <div className="container">
          <h2 className="section-title">✨ {t('product.mainFeatures', 'Features')} ✨</h2>
          <div className="features-grid">
            {productData.features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon" role="img" aria-label={feature.title}>
                  {feature.emoji || '⭐'}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI sections — optional detailed block, does not replace product intro */}
      {productData.aiIntroTitle && (
        <section className="product-ai-intro-section">
          <div className="container">
            <div className="product-ai-intro-content">
              <h2 className="section-title">{productData.aiIntroTitle}</h2>
              {productData.aiIntroTagline && (
                <p className="product-ai-intro-tagline">{productData.aiIntroTagline}</p>
              )}
              {productData.aiIntroDescription && (
                <p className="product-ai-intro-description">{productData.aiIntroDescription}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {productData.aiHighlights && productData.aiHighlights.length > 0 && (
        <section className="product-ai-highlights-section">
          <div className="container">
            {productData.aiHighlightsTitle && (
              <h2 className="section-title">{productData.aiHighlightsTitle}</h2>
            )}
            <div className="ai-highlights-grid">
              {productData.aiHighlights.map((highlight, index) => (
                <div key={index} className="ai-highlight-card">
                  <div className="ai-highlight-icon" role="img" aria-label={highlight.title}>
                    {highlight.emoji || '🤖'}
                  </div>
                  <h3 className="ai-highlight-title">{highlight.title}</h3>
                  <p className="ai-highlight-description">{highlight.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {productData.aiDeepDiveTitle && productData.aiDeepDiveParagraphs && productData.aiDeepDiveParagraphs.length > 0 && (
        <section className="product-ai-deepdive-section">
          <div className="container">
            <div className="product-ai-deepdive-content">
              <h2 className="section-title">{productData.aiDeepDiveTitle}</h2>
              {productData.aiDeepDiveParagraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className="product-ai-deepdive-text"
                  dangerouslySetInnerHTML={{ __html: paragraph }}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {productData.useCases && productData.useCases.length > 0 && (
        <section className="product-use-cases-section">
          <div className="container">
            {productData.useCasesTitle && (
              <h2 className="section-title">{productData.useCasesTitle}</h2>
            )}
            <div className="use-cases-grid">
              {productData.useCases.map((useCase, index) => (
                <div key={index} className="use-case-card">
                  <h3 className="use-case-scenario">{useCase.scenario}</h3>
                  <p className="use-case-benefit">{useCase.benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Play Section (if exists) */}
      {productData.whyPlayTitle && productData.whyPlayDescription && (
        <section className="product-why-section">
          <div className="container">
            <div className="product-why-content">
              <h2 className="section-title">{productData.whyPlayTitle}</h2>
              <p 
                className="product-why-text"
                dangerouslySetInnerHTML={{ __html: productData.whyPlayDescription }}
              />
            </div>
          </div>
        </section>
      )}

      {/* Video Section (if exists) */}
      {config.videoUrl && (
        <section className="product-video-section">
          <div className="container">
            <h2 className="section-title">{t('product.video', 'Promotional Video')}</h2>
            <div className="video-wrapper">
              <iframe
                width="100%"
                height="500"
                src={config.videoUrl.replace('watch?v=', 'embed/')}
                title={productData.name}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </section>
      )}

      {screenshots.length > 0 && (
        <section className="product-gallery-section">
          <div className="container">
            <h2 className="section-title">{t('product.screenshots', 'Screenshots')}</h2>
            <div className="product-screenshots-grid">
              {screenshots.map((screenshot, index) => (
                <div key={index} className="product-screenshot-card">
                  <div className="screenshot-image-wrapper">
                    <img src={screenshot.image} alt={screenshot.alt} />
                  </div>
                  <div className="screenshot-label">{getScreenshotLabel(screenshot, index)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="product-cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">{productData.sidebarTitle}</h2>
            <p className="cta-description">{productData.sidebarDescription}</p>
            <div className="cta-actions">
              {comingSoonStores ? (
                <>
                  {comingSoonButton('cta-play', t('product.viewOnPlayStore', 'View on Google Play'))}
                  {comingSoonButton('cta-appstore', t('product.viewOnAppStore', 'View on App Store'))}
                  {comingSoonButton('cta-web', t('product.tryOnWeb', 'Try it on Web'), 'btn-secondary')}
                </>
              ) : (
                <>
                  {comingSoonPlayStore
                    ? comingSoonButton('cta-play', t('product.viewOnPlayStore', 'View on Google Play'))
                    : config.playStoreUrl && (
                    <a
                      href={config.playStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-large"
                    >
                      {t('product.viewOnPlayStore', 'View on Google Play')}
                    </a>
                  )}
                  {config.appStoreUrl && (
                    <a
                      href={config.appStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-large"
                    >
                      {t('product.viewOnAppStore', 'View on App Store')}
                    </a>
                  )}
                </>
              )}
              {config.privacyUrl && (
                <Link 
                  to={config.privacyUrl}
                  className="btn btn-secondary btn-large"
                >
                  {t('footer.privacyPolicy', 'Privacy Policy')}
                </Link>
              )}
              {config.termsUrl && (
                <Link 
                  to={config.termsUrl}
                  className="btn btn-secondary btn-large"
                >
                  {t('footer.termsOfService', 'Terms of Service')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;

